import { useCallback, useEffect, useState } from 'react';
import { Table, Tag } from 'antd';
import { sendSingleRequest } from '../../apis';
import { Spinner, Title } from '../../components/shared';
import FilesViewModal from '../../components/shared/pictures-wall/FilesViewModal';

interface IIssue {
  id: number;
  content: string;
  images: string[] | null;
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED';
  created_at: string;
}

const statusColors = {
  OPEN: 'red',
  IN_PROGRESS: 'orange',
  RESOLVED: 'green',
  CLOSED: 'black',
};

const statusLabels = {
  OPEN: 'Open',
  IN_PROGRESS: 'In Progress',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
};

const UserIssuesPage = () => {
  const [issues, setIssues] = useState<IIssue[]>();
  const [loading, setLoading] = useState(false);
  const [selectedImages, setSelectedImages] = useState<string[]>();
  const [resolvingId, setResolvingId] = useState<number | null>(null);

  const loadIssues = useCallback(async () => {
    setLoading(true);
    const res = await sendSingleRequest({}, 'GET', 'api/issues', true);
    if (res.status) {
      setIssues(res.data);
    } else {
      window.alert(res.data?.message ?? 'Something went wrong');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadIssues();
  }, [loadIssues]);

  const resolveIssue = async (issueId: number) => {
    setResolvingId(issueId);
    const res = await sendSingleRequest(
      { status: 'RESOLVED' },
      'PUT',
      `api/issue/${issueId}`,
      true,
    );
    if (res.status) {
      loadIssues();
    } else {
      window.alert(res.data?.message ?? 'Something went wrong');
    }
    setResolvingId(null);
  };

  const columns = [
    {
      title: 'ID',
      key: 'id',
      render: (x: IIssue) => <span>{x.id}</span>,
    },
    {
      title: 'Content',
      key: 'content',
      render: (x: IIssue) => (
        <span style={{ whiteSpace: 'pre-wrap' }}>{x.content}</span>
      ),
    },
    {
      title: 'Images',
      key: 'images',
      render: (x: IIssue) =>
        x.images && x.images.length > 0 ? (
          <div
            className='btn__modal'
            onClick={() => setSelectedImages(x.images ?? [])}
          >
            View ({x.images.length})
          </div>
        ) : (
          <span>-</span>
        ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (x: IIssue) => (
        <div>
          <Tag color={statusColors[x.status]}>{statusLabels[x.status]}</Tag>
          {x.status === 'IN_PROGRESS' && (
            <div
              className='btn__modal pt-12'
              onClick={() => resolveIssue(x.id)}
              style={{ whiteSpace: 'nowrap' }}
            >
              {resolvingId === x.id ? <Spinner /> : 'Mark as Resolved'}
            </div>
          )}
        </div>
      ),
    },
    {
      title: '',
      key: 'created_at',
      render: (x: IIssue) => (
        <span style={{ whiteSpace: 'nowrap' }}>{x.created_at}</span>
      ),
    },
  ];

  return (
    <div className='bg-secondary min-height'>
      <div className='container'>
        <div className='queued-page-header d-flex justify-between align-center'>
          <Title size={5} color='black' align='default' fontWeight={700}>
            My tickets
          </Title>
        </div>
        <div className='w-100 pb-32'>
          {loading ? (
            <div className='d-flex justify-center align-center min-height'>
              <Spinner />
            </div>
          ) : (
            <Table
              rowKey='id'
              className='table table--isFarms'
              pagination={false}
              columns={columns}
              dataSource={issues ?? []}
            />
          )}
        </div>
      </div>
      {!!selectedImages && selectedImages.length > 0 && (
        <FilesViewModal
          visible={true}
          onClose={() => setSelectedImages(undefined)}
          files={selectedImages}
        />
      )}
    </div>
  );
};

export default UserIssuesPage;
